import { TreeItem, TreeItems } from 'dnd-kit-sortable-tree';
import {
  DocumentDeletionBlockReason,
  DocumentListItemDto,
  ProjectElement,
  Status,
  TemplateDto,
  TemplateListItemDto,
} from 'services/api/api-client';
import { QueryFactory } from 'services/api';
import {
  BaseFile,
  BaseFolder,
  BaseNode,
  FolderDeletionBlockReason,
  isFile,
  isFolder,
  NodeType,
} from './types/base';
import {
  BaseTemplateTreeNode,
  isTemplate,
  TemplateActivity,
  TemplateFolderNode,
  TemplateNode,
} from './types/templates';
import {
  BaseDocumentTreeNode,
  DocumentFolderNode,
  FolderStatus,
  isDocument,
  isDocumentFolder,
} from './types/documents';
import { assertNever } from 'helpers/assert-never';

export type UpdateDocumentItemsProps = {
  revisionId: number;
  projectId: number;
  onDelete: (folderId: string) => Promise<void>;
  onItemEdit: (
    id: string,
    editedName: string,
    editedProperty: 'name' | 'status',
  ) => Promise<void>;
  onAddSubItem: (folderId: string, node: BaseNode) => Promise<void>;
};

export type UpdateTemplateItemsProps = {
  onDelete: (folderId: string) => Promise<void>;
  onItemEdit: (
    id: string,
    editedName: string,
    editedProperty: 'name',
  ) => Promise<void>;
  onAddSubItem: (folderId: string, node: BaseNode) => Promise<void>;
};

export function checkIsExpandDisabled(items: TreeItems<BaseNode>): boolean {
  return items.every((i) => i.type !== NodeType.Folder);
}

export function expandAll(
  items: TreeItems<BaseNode> | BaseNode[],
  collapsed: boolean,
) {
  items.forEach((item) => {
    (item as TreeItem<BaseNode>).collapsed = collapsed;
    if (isFolder(item) && item.children.length > 0)
      expandAll(item.children, collapsed);
  });
}

export function editTreeItem(
  items: TreeItems<BaseNode> | BaseNode[],
  id: string,
  value: string | Status,
  propKey: 'name' | 'status',
) {
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.id === id) {
      if (propKey === 'name') {
        item.name = value;
      } else if (isDocument(item) && propKey === 'status') {
        item.status = value as Status;
      }
      return;
    } else if (isFolder(item) && item.children.length > 0)
      editTreeItem(item.children, id, value, propKey);
  }
}

export async function removeTreeItems(
  items: TreeItems<BaseNode> | BaseNode[],
  ids: string[],
  onNodeDelete?: (node: BaseNode) => Promise<void>,
) {
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (ids.includes(item.id)) {
      await onNodeDelete?.(item);
      items.splice(i, 1);
      i--;
    } else if (isFolder(item) && item.children.length > 0)
      await removeTreeItems(item.children, ids, onNodeDelete);
  }
}

/**
 * Updates folders' status depending on its children's status.
 * Set subfolders to collapsed if parent folder is collapsed.
 * @param items
 */
export function updateFolderProperties(
  items: TreeItems<BaseNode> | BaseNode[],
  parentCollapsed?: boolean,
) {
  const updatedItems = [...items];
  for (let i = 0; i < items.length; i++) {
    const folder = updatedItems[i];
    if (isFolder(folder)) {
      updateFolderProperties(folder.children, folder.collapsed);
      if (parentCollapsed) folder.collapsed = true;

      if (isDocumentFolder(folder)) {
        const notStarted = folder.children.every((child) => {
          return (
            isDocumentFolder(child) &&
            child.folderStatus === FolderStatus.NotStarted
          );
        });

        const notApplicable = folder.children.every((child) => {
          return (
            (isDocumentFolder(child) &&
              child.folderStatus === FolderStatus.NotApplicable) ||
            (isDocument(child) && child.status === Status.NotApplicable)
          );
        });

        const signed = folder.children.every((child) => {
          return (
            (isDocumentFolder(child) &&
              (child.folderStatus === FolderStatus.Signed ||
                child.folderStatus === FolderStatus.NotApplicable)) ||
            (isDocument(child) &&
              (child.status === Status.Signed ||
                child.status === Status.NotApplicable))
          );
        });

        if (folder.children.length === 0 || notStarted) {
          folder.folderStatus = FolderStatus.NotStarted;
        } else if (notApplicable) {
          folder.folderStatus = FolderStatus.NotApplicable;
        } else if (signed) {
          folder.folderStatus = FolderStatus.Signed;
        } else {
          folder.folderStatus = FolderStatus.InProgress;
        }
      }
    }
  }
  return updatedItems;
}

export function toProjectElements(
  items: TreeItems<BaseNode>,
): ProjectElement[] {
  return items.map((node) => {
    if (node.type === NodeType.Template) node.type = NodeType.Document;

    const newNode = {
      ...node,
      parent: undefined,
      documentId: isFile(node) ? node.documentId : undefined,
      children: node.children ? toProjectElements(node.children) : undefined,
    };

    return newNode;
  });
}

export function tryInsertInSpecificFolder(
  items: (BaseNode | ProjectElement)[],
  folderId: string,
  node: BaseNode | ProjectElement,
) {
  items.forEach((item) => {
    if (isFolder(item as BaseNode)) {
      const folder = item as BaseFolder;
      if (folder.id === folderId) {
        folder.children?.push(node as BaseNode);
        return;
      } else tryInsertInSpecificFolder(folder.children, folderId, node);
    }
  });

  return items;
}

export function getFilteredDocumentList(
  items: BaseNode[],
  statuses: Status[],
  searchingName: string,
  onlyNonUpdatedVersion: boolean,
): BaseNode[] {
  let filteredDocuments: BaseNode[] = [];

  filteredDocuments = searchDocumentsByStatus(items, statuses);
  filteredDocuments = searchDocumentsByName(filteredDocuments, searchingName);
  filteredDocuments = searchDocumentsByUpdatedVersion(
    filteredDocuments,
    onlyNonUpdatedVersion,
  );

  return filteredDocuments;
}

export function updateDocumentTreeProperties(
  items: BaseDocumentTreeNode[],
  files: DocumentListItemDto[],
  updateProps: UpdateDocumentItemsProps,
) {
  return (
    items
      ?.map((item) => {
        let file: DocumentListItemDto | null = null;

        if (isDocument(item)) {
          file = assignFileProperties(item, files);
          if (!file) return null!;
          item.status = file.status;
          item.isReadOnly = file.isReadOnly;
          item.isOpenXml = file.isOpenXml;
          item.version = file.version;
          item.expiredVariables = file.expiredVariables;
          item.templateWithUpdate = file.templateWithUpdate;
          item.versionUpdaterUser = file.versionUpdaterUser;
          item.auditData = file.auditData;
          item.date = file.date;
        } else if (isFolder(item)) {
          item.children = updateDocumentTreeProperties(
            item.children as BaseDocumentTreeNode[],
            files,
            updateProps,
          );
        }

        assignCanBeDeletedProperty(item, file);

        assignItemFunctions(
          item,
          updateProps.onDelete,
          updateProps.onItemEdit,
          updateProps.onAddSubItem,
        );

        item.projectId = updateProps.projectId;
        item.revisionId = updateProps.revisionId;
        updateFolderProperties(items);

        return item;
      })
      // remove nulls
      .filter((x) => !!x)
  );
}

export function updateTemplateTreeProperties(
  items: TreeItems<BaseTemplateTreeNode> | BaseTemplateTreeNode[],
  files: TemplateListItemDto[],
  updateProps: UpdateTemplateItemsProps,
) {
  return (
    items
      ?.map((item) => {
        if (isTemplate(item)) {
          const file = assignFileProperties(item, files);
          if (!file) return null!;
          item.canBeDeleted = true;
          item.activity = getTemplateActivity(file);
        } else if (isFolder(item)) {
          item.children = updateTemplateTreeProperties(
            item.children as BaseTemplateTreeNode[],
            files,
            updateProps,
          );
          item.canBeDeleted = true;
        }
        assignItemFunctions(
          item,
          updateProps.onDelete,
          updateProps.onItemEdit,
          updateProps.onAddSubItem,
        );

        updateFolderProperties(items);

        return item;
      })
      // remove nulls
      .filter((x) => !!x)
  );
}

function assignCanBeDeletedProperty(
  item: BaseNode,
  sourceItem?: {
    canBeDeleted?: boolean;
  } | null,
) {
  if (isFolder(item)) {
    const lockedChildren = item.children.find((child) => !child.canBeDeleted);
    if (lockedChildren) {
      item.canBeDeleted = false;
      item.deletionBlockReason = FolderDeletionBlockReason.HasBlockedChildren;
    } else {
      item.canBeDeleted = true;
    }
  } else if (isFile(item)) {
    item.canBeDeleted = sourceItem?.canBeDeleted;
  }
}

export function assignItemFunctions(
  item: TreeItem<BaseNode>,
  onDelete: (folderId: string) => Promise<void>,
  onItemEdit: (
    id: string,
    editedName: string,
    editedProperty: any,
  ) => Promise<void>,
  onAddSubItem: (folderId: string, node: BaseNode) => Promise<void>,
) {
  item.onDelete = onDelete;

  if (isFolder(item)) {
    item.onAddSubItem = onAddSubItem;
    item.onFolderEdit = onItemEdit;
  } else if (isDocument(item)) {
    item.onDocumentEdit = onItemEdit;
  }
}

function assignFileProperties<
  T extends {
    id: number;
    initialId: number | null;
    isExported: boolean;
    deletionBlockReason?: DocumentDeletionBlockReason;
  },
>(item: BaseFile, files: T[]): T | null {
  const file = files.find((doc) => doc.id === item.documentId);
  if (!file) return null;

  item.id = String(file.initialId ?? file.id);
  item.documentId = file.id;
  item.isExported = file.isExported;
  item.canHaveChildren = false;
  item.deletionBlockReason = file.deletionBlockReason;

  return file;
}

export function createDocumentFolderNode(
  id: string,
  name: string,
  revisionId: number,
  projectId: number,
): DocumentFolderNode {
  return {
    id,
    projectId,
    revisionId,
    type: NodeType.Folder,
    name,
    folderStatus: FolderStatus.NotStarted,
    children: [],
    collapsed: true,
    onDelete: async () => {},
    onFolderEdit: async () => {},
    onAddSubItem: async () => {},
  };
}

export function createTemplateNode(template: TemplateDto): TemplateNode {
  return {
    id: String(template.initialId ?? template.id),
    documentId: template.id,
    type: NodeType.Template,
    name: template.name,
    onDelete: async () => {},
    onTemplateEdit: async () => {},
    isExported: template.isExported,
    canBeDeleted: true,
    activity: getTemplateActivity(template),
  };
}

function getTemplateActivity(
  template: TemplateDto | TemplateListItemDto,
): TemplateActivity | null {
  return template.auditData?.created
    ? {
        date: template.auditData.created.date,
        user: template.auditData.created.byName,
        action: template.id === template.initialId ? 'import' : 'update',
      }
    : null;
}

export function createTemplateFolderNode(
  id: string,
  name: string,
): TemplateFolderNode {
  return {
    id,
    type: NodeType.Folder,
    name,
    children: [],
    collapsed: true,
    onDelete: async () => {},
    onFolderEdit: async () => {},
    onAddSubItem: async () => {},
  };
}

export function isSameDepth(
  fromParent: TreeItem<BaseNode> | null,
  toParent: TreeItem<BaseNode> | null,
): boolean {
  if (!fromParent && !toParent) return true;
  if ((!fromParent && toParent) || (!toParent && fromParent)) return false;

  const fromParentDepth = (fromParent as any).depth;
  const toParentDepth = (toParent as any).depth;

  return fromParentDepth === toParentDepth;
}

export function isItemExported(item: TreeItem<BaseNode>): boolean {
  if (isFile(item) && item.isExported) return true;

  if (item.children && item.children.length > 0) {
    for (let index = 0; index < item.children.length; index++) {
      if (isItemExported(item.children![index])) return true;
    }
  }

  return false;
}

export function getItemById(
  id: string,
  items: BaseNode[],
): TreeItem<BaseNode> | undefined {
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.id === id) return item;

    if (isFolder(item) && item.children.length > 0) {
      const resultItem = getItemById(id, item.children!);
      if (resultItem) return resultItem;
    }
  }
  return undefined;
}

export function replaceItemById(
  id: string,
  items: BaseNode[],
  newItem?: BaseNode,
): BaseNode[] {
  const newItems: BaseNode[] = [];

  for (let i = 0; i < items.length; i++) {
    const item = items[i];

    if (item.id === id) {
      if (newItem) newItems.push(newItem);
      continue;
    }

    if (isFolder(item) && item.children.length > 0) {
      const result = replaceItemById(id, item.children, newItem);
      newItems.push({
        ...item,
        children: result,
      } as BaseFolder);
      continue;
    }

    newItems.push(item);
  }

  return newItems;
}

export async function setItemUnexported(item: TreeItem<BaseNode>) {
  if (isDocument(item) && item.isExported) {
    await QueryFactory.DocumentClient.patch(item.documentId, {
      isExported: false,
    });
  }

  if (item.children && item.children.length > 0) {
    item.children?.forEach((item) => setItemUnexported(item));
  }
}

function searchDocumentsByUpdatedVersion(
  items: TreeItems<BaseNode> | BaseNode[],
  onlyNonUpdatedVersion: boolean,
): BaseNode[] {
  if (!onlyNonUpdatedVersion) return items;

  const filteredDocuments: BaseNode[] = [];
  items.forEach((item) => {
    if (isDocument(item) && !item.versionUpdaterUser) {
      const searchedItem = { ...item };
      searchedItem.disableSorting = true;
      filteredDocuments.push(searchedItem);
    } else if (isFolder(item) && item.children.length > 0)
      filteredDocuments.push(
        ...searchDocumentsByUpdatedVersion(
          item.children!,
          onlyNonUpdatedVersion,
        ),
      );
  });

  return filteredDocuments;
}

function searchDocumentsByStatus(
  items: TreeItems<BaseNode> | BaseNode[],
  statuses: Status[],
): BaseNode[] {
  if (statuses.length === 0) return items;

  const filteredDocuments: BaseNode[] = [];
  items.forEach((item) => {
    if (isDocument(item) && statuses.includes(item.status as Status)) {
      const searchedItem = { ...item };
      searchedItem.disableSorting = true;
      filteredDocuments.push(searchedItem);
    } else if (isFolder(item) && item.children.length > 0)
      filteredDocuments.push(
        ...searchDocumentsByStatus(item.children!, statuses),
      );
  });

  return filteredDocuments;
}

function searchDocumentsByName(
  items: TreeItems<BaseNode> | BaseNode[],
  searchingName: string,
): BaseNode[] {
  if (searchingName === '') return items;

  const searchedDocuments: BaseNode[] = [];
  items.forEach((item) => {
    if (
      isFile(item) &&
      item.name.toLowerCase().includes(searchingName.toLowerCase())
    ) {
      const searchedItem = { ...item };
      searchedItem.disableSorting = true;
      searchedDocuments.push(searchedItem);
    } else if (isFolder(item) && item.children.length > 0)
      searchedDocuments.push(
        ...searchDocumentsByName(item.children!, searchingName),
      );
  });

  return searchedDocuments;
}
